/**
 * Per-participant ink colour.
 *
 * The colour is the only label a stroke carries: nobody wants a name tag on
 * every line, so "the red one is Petrov" has to hold for a whole call. Two
 * requirements follow — every client must compute the same colour for the same
 * person, and no two people in the room may share one.
 *
 * ⚠️ Hashing the id straight onto a palette satisfies the first and badly
 * fails the second: with eight colours and six people the chance that all six
 * come out distinct is under ten percent. So the hash only picks a *preferred*
 * slot and collisions are resolved by scanning forward over a roster that
 * every client sorts identically.
 */

/**
 * Vivid mid-lightness hues, readable both on a white CAD sheet and on a dark
 * editor — ink lands on whatever the presenter happens to be showing, so
 * anything pale or muddy is out.
 */
export const INK_PALETTE = [
  "#e5484d", // red
  "#f76b15", // orange
  "#e2a336", // amber
  "#46a758", // green
  "#12a594", // teal
  "#00a2c7", // cyan
  "#3e63dd", // blue
  "#8e4ec6", // violet
  "#d6409f", // pink
  "#5c7f2f", // olive
] as const;

/** FNV-1a, 32-bit. Small, dependency-free and identical in every client. */
export function hashIdentity(identity: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < identity.length; i++) {
    hash ^= identity.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

/**
 * Colour for every participant, distinct as long as the room is no larger than
 * the palette.
 *
 * Order of the input does not matter — the roster is sorted first, so two
 * clients that learned about participants in different orders still agree.
 * A colour can shift when somebody joins or leaves; that is the price of
 * guaranteed distinctness and it is the cheaper of the two annoyances.
 */
export function assignInkColors(identities: string[]): Map<string, string> {
  const roster = [...new Set(identities)].sort();
  const taken = new Array<string | undefined>(INK_PALETTE.length);
  const result = new Map<string, string>();

  for (const identity of roster) {
    const preferred = hashIdentity(identity) % INK_PALETTE.length;

    let slot = -1;
    for (let step = 0; step < INK_PALETTE.length; step++) {
      const candidate = (preferred + step) % INK_PALETTE.length;
      if (!taken[candidate]) {
        slot = candidate;
        break;
      }
    }

    // Room larger than the palette: fall back to the preferred slot and let it
    // repeat. Sharing a colour beats leaving somebody's ink uncoloured.
    if (slot === -1) slot = preferred;

    taken[slot] = identity;
    result.set(identity, INK_PALETTE[slot]);
  }

  return result;
}
